/**
 * `.rbxcloth` codec — a zip whose metadata is separable from its pixels.
 *
 * The layout matters for cost, not just tidiness: the project list, the
 * proofing page, and the colourway generator all need `manifest`, `palette`
 * and `layers` but none of the bitmaps. Keeping them as separate entries means
 * those paths read a few KB instead of a few MB.
 *
 *   manifest.json      formatVersion, garmentType, garments[], canvas, timestamps
 *   palette.json       colour slots
 *   layers.json        ordered layer list, all parameters, NO pixels — PRIMARY garment
 *   colourways.json    named override maps
 *   edge.json          edge-integrity settings — PRIMARY garment
 *   guides.json        horizontal rules, project-level          (v3+, optional)
 *   garments/<g>/layers.json   the ADDITIONAL garments          (v3+, optional)
 *   garments/<g>/edge.json                                      (v3+, optional)
 *   layers/<id>.png    raster / image payloads only, flat across every garment
 *   thumb.webp         128px preview
 *
 * Bitmaps stay flat and un-namespaced because layer ids are unique across
 * garments — `newId()` in src/app/layers.ts is module-scoped, so two stacks in
 * one document cannot mint the same id. That is load-bearing for this layout,
 * and there is a test on it.
 */
import { unzipSync, zipSync } from "fflate";
import {
  type Colourway,
  DEFAULT_EDGE_SETTINGS,
  type EdgeSettings,
  FORMAT_VERSION_OUTFIT,
  type GarmentDoc,
  type GarmentType,
  type Guide,
  MAX_VARIANTS,
  type Manifest,
  migrateLayers,
  type Palette,
  type Project,
  type Variant,
} from "./format";

export interface PackedExtras {
  /** Raster payloads keyed by the name used in layers/<name> */
  blobs?: Record<string, Uint8Array>;
  thumb?: Uint8Array;
}

const ENC = new TextEncoder();
const DEC = new TextDecoder();

function json(value: unknown): Uint8Array {
  return ENC.encode(JSON.stringify(value));
}

function parse<T>(bytes: Uint8Array | undefined, what: string): T {
  if (!bytes) throw new PackError(`missing ${what}`);
  try {
    return JSON.parse(DEC.decode(bytes)) as T;
  } catch (err) {
    throw new PackError(`${what} is not valid JSON: ${(err as Error).message}`);
  }
}

export class PackError extends Error {
  override name = "PackError";
}

export function packProject(project: Project, extras: PackedExtras = {}): Uint8Array {
  const files: Record<string, Uint8Array> = {
    "manifest.json": json(project.manifest),
    "palette.json": json(project.palette),
    "layers.json": json(project.layers),
    "colourways.json": json(project.colourways),
    "edge.json": json(project.edge),
  };
  // Written only when present, so a plain garment's archive is unchanged by the
  // existence of the rank-set feature.
  if (project.variants?.length) files["variants.json"] = json(project.variants);

  /*
   * The additional garments, and *only* the additional ones.
   *
   * The primary stays in the root `layers.json`/`edge.json` it has always been
   * in. That is not tidiness — `peekManifest` requires root `layers.json`, and
   * it is the gate every upload passes through in the Worker. Moving the primary
   * under `garments/` would break the cheap path for every archive at once.
   *
   * So the layout is: root = the v2-compatible view, `garments/` = the
   * extension. Nothing is written twice, so the two cannot disagree.
   */
  const primary = project.manifest.garmentType;
  const extra = Object.entries(project.garments ?? {}).filter(
    ([g, doc]) => g !== primary && doc !== undefined,
  );
  if (extra.length) {
    // The invariant enforced rather than documented. A v3 archive against the
    // deployed v2 Worker fails every save for everyone, so the unsafe
    // combination is made unwritable instead of discouraged in a comment.
    if (project.manifest.formatVersion < FORMAT_VERSION_OUTFIT) {
      throw new PackError(
        `project declares ${extra.length} extra garment(s) but the manifest claims format ` +
          `v${project.manifest.formatVersion}; a second garment needs v${FORMAT_VERSION_OUTFIT}. ` +
          "Ship the Worker first — see FORMAT_VERSION in shared/format.ts.",
      );
    }
    for (const [g, doc] of extra) {
      files[`garments/${g}/layers.json`] = json((doc as GarmentDoc).layers);
      files[`garments/${g}/edge.json`] = json((doc as GarmentDoc).edge);
    }
  }
  if (project.guides?.length) files["guides.json"] = json(project.guides);
  for (const [name, bytes] of Object.entries(extras.blobs ?? {})) {
    files[`layers/${name}`] = bytes;
  }
  if (extras.thumb) files["thumb.webp"] = extras.thumb;

  // level 0 for already-compressed payloads; the JSON still benefits.
  return zipSync(files, { level: 6 });
}

export interface UnpackedProject {
  project: Project;
  blobs: Record<string, Uint8Array>;
  thumb?: Uint8Array;
  /**
   * Layers an older archive lost on the way in, described for a human.
   *
   * Returned rather than logged because the only honest place to say "four
   * layers from this project no longer exist" is in front of the person who
   * made them.
   */
  droppedOnMigration: string[];
}

export function unpackProject(bytes: Uint8Array): UnpackedProject {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch (err) {
    throw new PackError(`not a valid .rbxcloth archive: ${(err as Error).message}`);
  }

  const manifest = parse<Manifest>(files["manifest.json"], "manifest.json");
  if (typeof manifest.formatVersion !== "number") {
    throw new PackError("manifest.formatVersion is missing");
  }
  /*
   * Read up to v3, write v2. That asymmetry *is* "Worker first".
   *
   * The rule in format.ts is that a client emitting a version the Worker cannot
   * read fails every save for everyone. The way to obey it is to ship the
   * ability to *read* the new version before anything *writes* it — so this
   * bound is FORMAT_VERSION_OUTFIT while `FORMAT_VERSION`, which is what writers
   * stamp into a manifest, is still 2.
   */
  if (manifest.formatVersion > FORMAT_VERSION_OUTFIT) {
    throw new PackError(
      `archive is format v${manifest.formatVersion} but this build understands up to v${FORMAT_VERSION_OUTFIT}`,
    );
  }

  const blobs: Record<string, Uint8Array> = {};
  for (const [path, bytes_] of Object.entries(files)) {
    if (path.startsWith("layers/")) blobs[path.slice("layers/".length)] = bytes_;
  }

  // Migrate rather than cast.
  //
  // The version guard above only rejects archives from the *future*. An older
  // archive sails straight through — `1 > 2` is false — and JSON carries no
  // runtime type information, so `parse<Layer[]>` would have been a lie the
  // compiler is happy to tell: v1 objects wearing the v2 type, failing later at
  // whatever first reads `.kind`. Keyed off the manifest, never off the shape.
  const migration = migrateLayers(
    manifest.formatVersion,
    parse<unknown>(files["layers.json"], "layers.json"),
  );

  const variants = files["variants.json"]
    ? parse<Variant[]>(files["variants.json"], "variants.json")
    : [];
  // Bounding bytes does not bound element count — a few MB of `{}` is a very
  // large array. Same reasoning as the Worker's MAX_LAYERS.
  if (!Array.isArray(variants)) throw new PackError("variants.json is not an array");
  if (variants.length > MAX_VARIANTS) {
    throw new PackError(
      `archive declares ${variants.length} variants; the limit is ${MAX_VARIANTS}`,
    );
  }

  const edge: EdgeSettings = files["edge.json"]
    ? parse<EdgeSettings>(files["edge.json"], "edge.json")
    : { ...DEFAULT_EDGE_SETTINGS };

  /*
   * Reassemble every garment.
   *
   * The primary comes from the root entries just read; the rest from
   * `garments/`. Each one goes through `migrateLayers` for the same reason the
   * primary does — an old archive's layers are old wherever they are stored, and
   * reading backwards is silent.
   *
   * `layers` and `edge` on the returned Project are the primary's **by
   * reference**: the same array and the same object, not copies, so a caller
   * mutating `project.layers` mutates `garments[primary].layers` because there
   * is only one of them. See the note on Project.garments in format.ts.
   */
  const dropped = [...migration.dropped];
  const garments: Partial<Record<GarmentType, GarmentDoc>> = {
    [manifest.garmentType]: { layers: migration.layers, edge },
  };
  for (const [path, raw] of Object.entries(files)) {
    const match = /^garments\/([^/]+)\/layers\.json$/.exec(path);
    if (!match) continue;
    const g = match[1] as GarmentType;
    if (g === manifest.garmentType) {
      // The primary lives at the root. Two copies could disagree, and there
      // would be no principled way to pick a winner.
      throw new PackError(
        `archive stores the primary garment "${g}" under garments/ as well as at the root`,
      );
    }
    const m = migrateLayers(manifest.formatVersion, parse<unknown>(raw, path));
    dropped.push(...m.dropped);
    garments[g] = {
      layers: m.layers,
      edge: files[`garments/${g}/edge.json`]
        ? parse<EdgeSettings>(files[`garments/${g}/edge.json`], `garments/${g}/edge.json`)
        : { ...DEFAULT_EDGE_SETTINGS },
    };
  }

  const guides = files["guides.json"] ? parse<Guide[]>(files["guides.json"], "guides.json") : [];
  if (!Array.isArray(guides)) throw new PackError("guides.json is not an array");

  return {
    project: {
      manifest,
      palette: parse<Palette>(files["palette.json"], "palette.json"),
      layers: migration.layers,
      colourways: files["colourways.json"]
        ? parse<Colourway[]>(files["colourways.json"], "colourways.json")
        : [],
      ...(variants.length ? { variants } : {}),
      edge,
      garments,
      ...(guides.length ? { guides } : {}),
    },
    blobs,
    thumb: files["thumb.webp"],
    /** Non-empty when an older archive lost layers on the way in. */
    droppedOnMigration: dropped,
  };
}

/**
 * Largest archive we will accept. Comfortably above a real project (a heavy one
 * is a few MB) and far below anything that threatens a Worker.
 */
export const MAX_ARCHIVE_BYTES = 32 * 1024 * 1024;

/**
 * Largest a single metadata entry may claim to decompress to.
 *
 * A zip bomb is a tiny archive whose entries expand to gigabytes. The zip's own
 * central directory declares each entry's uncompressed size, so we can refuse
 * one *before* decompressing rather than discovering it by running out of
 * memory. These entries are JSON describing layers — a megabyte is already
 * generous.
 */
export const MAX_METADATA_BYTES = 4 * 1024 * 1024;

/** More layers than any real garment, and a cheap ceiling on parse cost. */
export const MAX_LAYERS = 500;

/**
 * Read only the small entries. Used by the project list and any server-side
 * entitlement check, so neither has to pull megabytes of bitmap out of R2.
 *
 * Also the gate every upload passes through, so the hostile-input checks live
 * here rather than being repeated at each call site.
 */
export function peekManifest(bytes: Uint8Array): { manifest: Manifest; layerCount: number } {
  if (bytes.byteLength > MAX_ARCHIVE_BYTES) {
    throw new PackError(`archive is ${bytes.byteLength} bytes; the limit is ${MAX_ARCHIVE_BYTES}`);
  }

  // variants.json is in this list on purpose. Left out, the count cap below
  // could never fire — the entry would simply never be extracted, and the guard
  // would read as protection while protecting nothing.
  //
  // `garments/<g>/layers.json` is here for the same reason and it matters more:
  // the cap is what stops an archive declaring a million layers, and counting
  // only the root entry would leave a second garment as an uncapped place to
  // put them. An outfit's layers are counted together because the limit is
  // about the archive, not about a garment.
  const wanted = new Set(["manifest.json", "layers.json", "variants.json"]);
  const isGarmentLayers = (name: string) => /^garments\/[^/]+\/layers\.json$/.test(name);
  const files = unzipSync(bytes, {
    filter: (f) => {
      if (!wanted.has(f.name) && !isGarmentLayers(f.name)) return false;
      // Declared size, checked before any decompression happens.
      if (f.originalSize > MAX_METADATA_BYTES) {
        throw new PackError(`${f.name} declares ${f.originalSize} bytes; refusing to expand it`);
      }
      return true;
    },
  });

  const manifest = parse<Manifest>(files["manifest.json"], "manifest.json");
  // `unknown[]`, not `Layer[]`: this path only counts. Typing it as the layer
  // union would assert a shape nobody has validated, on attacker-controlled
  // JSON, in the cheap path whose whole job is to reject before doing real work.
  const layers = parse<unknown[]>(files["layers.json"], "layers.json");

  if (!Array.isArray(layers)) throw new PackError("layers.json is not an array");

  // Every garment's layers, against one cap. Summed before the check rather
  // than capped per garment: two garments of 400 layers each is the same
  // problem for a reader as one garment of 800.
  let layerCount = layers.length;
  for (const [path, raw] of Object.entries(files)) {
    if (!isGarmentLayers(path)) continue;
    const extra = parse<unknown[]>(raw, path);
    if (!Array.isArray(extra)) throw new PackError(`${path} is not an array`);
    layerCount += extra.length;
  }

  if (layerCount > MAX_LAYERS) {
    throw new PackError(`archive declares ${layerCount} layers; the limit is ${MAX_LAYERS}`);
  }

  // Same treatment for variants: MAX_METADATA_BYTES bounds the bytes but not the
  // element count, and a rank set is a second unbounded array.
  if (files["variants.json"]) {
    const variants = parse<unknown[]>(files["variants.json"], "variants.json");
    if (!Array.isArray(variants)) throw new PackError("variants.json is not an array");
    if (variants.length > MAX_VARIANTS) {
      throw new PackError(
        `archive declares ${variants.length} variants; the limit is ${MAX_VARIANTS}`,
      );
    }
  }

  // Across every garment, so the number the Worker stores beside a version is
  // the size of the design rather than the size of half of it.
  return { manifest, layerCount };
}
